<template>
	<div
		v-if="offerActivityQuest"
		class="piggy-bank-offer-card-detail template-layout"
	>
		<div
			v-if="questCompleted"
			class="piggy-bank-offer-card-detail__content piggy-bank-offer-card-detail__content--completed"
		>
			<div class="progress-sub-info">
				<span class="piggy-bank-offer-card-detail__content-cta">
					{{ translate("ui.open_treasury_cta") }}
				</span>
				<div class="rewards-grid__label">
					<AppPrice
						:amount="+offerActivityQuest.objective.amount"
						:currency="
							offerActivityQuest.reward.content?.[0]
								?.reward_resource as ResourceType
						"
						:fontSize="24"
						:iconSize="24"
					/>
				</div>
			</div>

			<div
				:class="`piggy-bank-offer-card-detail__content-treasure offer-resource-icon--${contentData.attributes?.skin}--full-208 flipped`"
			/>
		</div>
		<div v-else class="piggy-bank-offer-card-detail__content">
			<div class="progresable-treasure" />

			<div class="piggy-bank-offer-card-detail__content-progress">
				<span class="piggy-bank-offer-card-detail__content-cta">
					{{ translate("ui.fill_treasury_cta") }}
				</span>

				<div class="progress-sub-info">
					<span class="progress-sub-info__current">
						{{ offerActivityQuest.progress }}
					</span>
					<span class="progress-sub-info__separator">/</span>
					<AppPrice
						:amount="+offerActivityQuest.objective.amount"
						:currency="
							offerActivityQuest?.reward?.content?.[0]
								?.reward_resource as ResourceType
						"
						class="progress-sub-info__target"
						:resourceCheck="false"
						:fontSize="24"
						:iconSize="24"
					/>
				</div>

				<AppProgressBar :progress="progress" />
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import AppProgressBar from "@/components/AppProgressBar.vue";
import type { ResourceType } from "@/game/entities/config/resources";
import useQuestStore from "@/store/useQuestStore";
import type { QuestActive } from "@/types/quest";
import { type ShopItem } from "@/types/shop";
import { floorNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const questStore = useQuestStore();
const { getActiveQuests: activeQuests } = storeToRefs(questStore);

const offerActivityQuest = computed((): QuestActive | undefined =>
	activeQuests.value.find(
		(q: QuestActive): boolean =>
			"activity" in contentData.attributes &&
			q.id === contentData.attributes?.activity,
	),
);

const progress = computed((): number => {
	const quest = offerActivityQuest.value;

	if (!quest?.objective?.amount) return 0;

	const ratio = +quest.progress / +quest.objective.amount;
	return ratio >= 1 ? 100 : floorNumber(ratio * 100);
});

const questCompleted = computed((): boolean => progress.value >= 100);
</script>

<style lang="scss" scoped>
.piggy-bank-offer-card-detail {
	@include flex-row-center;
	overflow-x: clip;
	overflow-y: visible;

	&__content {
		position: relative;
		flex: 1 1 auto;
		width: 100%;
		min-height: 0;
		@include flex-row-center;
		min-height: 6.125rem;

		&--completed {
			padding-right: 10.625rem;

			.progress-sub-info {
				@include flex-col-center;
				gap: 0.625rem;
				padding-left: 2.375rem;
				text-align: center;
			}
		}

		&-treasure {
			position: absolute;
			top: -24px;
			right: -36px;
		}

		.progresable-treasure {
			position: relative;
			background: #fff;
			width: 7.125rem;
			height: 7rem;
			@include background(
				url(#{$path-images} + "shop/backgrounds/templates/piggy-bank-offer-rewards-sprite--h690-progress.avif"),
				auto 100%,
				center,
				no-repeat
			);
			flex-shrink: 0;
		}

		&-cta {
			color: #f4d278;
			font-weight: 800;
			font-size: 0.875rem;
			line-height: 1;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		&-progress {
			padding: 1rem;
			width: 100%;
			@include flex-col-center;
			align-items: flex-start;

			.progress-bar {
				height: 0.5rem;
			}

			.progress-sub-info {
				@include flex-row-center;
				font-weight: 800;
				font-size: 1.5rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);

				&__current {
					margin: 0 0.2rem;
					color: #ffc466;
				}

				&__separator,
				&__target :deep(.shrink-text) {
					color: #fff3dd;
				}
			}
		}

		.flipped {
			transform: scaleX(-1);
		}

		.rewards-grid__label {
			@include flex-row-center;
			position: relative;
			-webkit-mask-image: linear-gradient(
				to right,
				transparent 2%,
				black 20%,
				black 80%,
				transparent 98%
			);
			mask-image: linear-gradient(
				to right,
				transparent 2%,
				black 20%,
				black 80%,
				transparent 98%
			);
			border-width: 0.063rem 0 0.063rem 0;
			border-style: solid;
			border-color: #cd912d;
			border-image-slice: 1;
			width: 100%;

			:deep(.price .shrink-text) {
				padding-bottom: 0.25rem;
			}

			&::after {
				position: absolute;
				z-index: -1;
				background: rgb(100, 11, 11);
				width: 100%;
				height: 100%;
				content: "";
			}
		}
	}
}
</style>
